import { useCallback, useEffect, useState } from 'react';
import { ActiveSelection, Canvas, FabricObject, Group } from 'fabric';
import { arrangeGroup } from '../collection/arrange';

type UseGroupingReturn = {
  canGroup: boolean;
  canUngroup: boolean;
  groupSelection: () => void;
  ungroupSelection: () => void;
  createGridCollection: () => void;
};

/**
 * Wraps a multi-step operation so the history hook records it as one entry.
 * Falls back to running the function directly when no transaction is given,
 * which only impacts history granularity (functionality is unchanged).
 */
type Transaction = (fn: () => void | Promise<void>) => Promise<void> | void;
const runStep = (transaction: Transaction | undefined, fn: () => void) => (transaction ? transaction(fn) : fn());

const isGroup = (obj: FabricObject | null | undefined): obj is Group => !!obj && obj.type === 'group';

const isActiveSelection = (obj: FabricObject | null | undefined): obj is ActiveSelection =>
  !!obj && obj.type === 'activeselection';

/**
 * Per-member `objectCaching` snapshot at the moment of grouping, so ungroup
 * restores the exact original state regardless of per-class defaults.
 */
const memberCachingSnapshot = new WeakMap<FabricObject, boolean>();

export const useGrouping = (canvas: Canvas | null, transaction?: Transaction): UseGroupingReturn => {
  const [canGroup, setCanGroup] = useState(false);
  const [canUngroup, setCanUngroup] = useState(false);

  const sync = useCallback(() => {
    if (!canvas) {
      setCanGroup(false);
      setCanUngroup(false);
      return;
    }
    const active = canvas.getActiveObject();
    setCanGroup(isActiveSelection(active) && active.getObjects().length >= 2);
    setCanUngroup(isGroup(active));
  }, [canvas]);

  useEffect(() => {
    if (!canvas) return;
    canvas.on('selection:created', sync);
    canvas.on('selection:updated', sync);
    canvas.on('selection:cleared', sync);
    canvas.on('object:added', sync);
    canvas.on('object:removed', sync);
    sync();
    return () => {
      canvas.off('selection:created', sync);
      canvas.off('selection:updated', sync);
      canvas.off('selection:cleared', sync);
      canvas.off('object:added', sync);
      canvas.off('object:removed', sync);
    };
  }, [canvas, sync]);

  const groupActiveSelection = useCallback(
    (configure?: (group: Group) => void) => {
      if (!canvas) return;
      const active = canvas.getActiveObject();
      if (!isActiveSelection(active)) return;

      const members = active.getObjects();
      if (members.length < 2) return;

      const stack = canvas.getObjects();
      const ordered = [...members].sort((a, b) => stack.indexOf(a) - stack.indexOf(b));

      const targetIndex = Math.max(...members.map(o => stack.indexOf(o)));

      runStep(transaction, () => {
        canvas.discardActiveObject();
        ordered.forEach(o => canvas.remove(o));

        ordered.forEach(m => {
          memberCachingSnapshot.set(m, m.objectCaching ?? true);
          m.objectCaching = false;
        });

        const group = new Group(ordered, { subTargetCheck: false, objectCaching: false });
        configure?.(group);

        canvas.add(group);
        canvas.moveObjectTo(group, targetIndex);
        canvas.setActiveObject(group);
        canvas.requestRenderAll();
      });
      sync();
    },
    [canvas, sync, transaction]
  );

  const groupSelection = useCallback(() => groupActiveSelection(), [groupActiveSelection]);

  const createGridCollection = useCallback(
    () =>
      groupActiveSelection(group => {
        group.groupCollection = {
          type: 'grid',
          width: group.width,
          height: group.height,
          rowGap: 16,
          colGap: 16
        };
        arrangeGroup(group);
      }),
    [groupActiveSelection]
  );

  const ungroupSelection = useCallback(() => {
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!isGroup(active)) return;

    runStep(transaction, () => {
      const insertAt = canvas.getObjects().indexOf(active);
      const children = active.removeAll() as FabricObject[];
      canvas.remove(active);

      children.forEach((child, i) => {
        // Restore caching; fall back to `true` for children loaded from JSON.
        child.objectCaching = memberCachingSnapshot.get(child) ?? true;
        memberCachingSnapshot.delete(child);
        canvas.add(child);
        canvas.moveObjectTo(child, insertAt + i);
      });

      canvas.discardActiveObject();
      canvas.requestRenderAll();
    });
    sync();
  }, [canvas, sync, transaction]);

  return { canGroup, canUngroup, groupSelection, ungroupSelection, createGridCollection };
};
